import { auth } from '../firebase';

const FUNCTIONS_BASE = 'https://asia-south1-in-solidai-myadvocate.cloudfunctions.net';

export const AI_TRIAL_CREDITS = 300;
export const AI_TRIAL_DAYS = 14;
export const AI_PLUS_PRICE_INR = 299;
export const AI_PLUS_INCLUDED_CREDITS = 1500;
export const AI_TOPUP_PACKS = [
  { id: 'starter_500', amountInr: 99, credits: 500 },
  { id: 'growth_1500', amountInr: 249, credits: 1500 },
  { id: 'pro_3500', amountInr: 499, credits: 3500 },
];

const authorizedPost = async (path, body = {}) => {
  const token = await auth.currentUser?.getIdToken();
  if (!token) {
    throw new Error('You must be signed in to use AI billing.');
  }

  const response = await fetch(`${FUNCTIONS_BASE}/${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.error || 'Unable to complete the billing request.');
  }

  return data;
};

let razorpayScriptPromise;

const loadRazorpayScript = () => {
  if (window.Razorpay) {
    return Promise.resolve(true);
  }

  if (razorpayScriptPromise) {
    return razorpayScriptPromise;
  }

  razorpayScriptPromise = new Promise((resolve) => {
    const existing = document.querySelector('script[data-razorpay-checkout="true"]');
    if (existing) {
      existing.addEventListener('load', () => resolve(true), { once: true });
      existing.addEventListener('error', () => resolve(false), { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    script.dataset.razorpayCheckout = 'true';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

  return razorpayScriptPromise;
};

const openRazorpayCheckout = (options) =>
  new Promise((resolve, reject) => {
    if (!window.Razorpay) {
      reject(new Error('Unable to load Razorpay checkout.'));
      return;
    }

    const razorpay = new window.Razorpay({
      ...options,
      handler: (response) => resolve(response),
      modal: {
        ondismiss: () => reject(new Error('Payment cancelled by user.')),
      },
    });

    razorpay.open();
  });

export const hasAiCredits = (summary = {}) =>
  (Number(summary.trialCreditsRemaining) || 0) +
    (Number(summary.includedCreditsRemaining) || 0) +
    (Number(summary.walletCreditsRemaining) || 0) > 0;

export const hasAiEntitlement = (summary = {}) =>
  Boolean(summary.hasEntitlement) || ['trial', 'ai_plus'].includes(summary.planTier);

export const canUseAiNow = (summary = {}) =>
  Boolean(summary.canUseAiNow) || (hasAiEntitlement(summary) && hasAiCredits(summary));

export const getAiCreditHeadline = (summary = {}) => {
  if ((Number(summary.trialCreditsRemaining) || 0) > 0) {
    return `${summary.trialCreditsRemaining} trial credits left`;
  }
  if ((Number(summary.includedCreditsRemaining) || 0) > 0) {
    return `${summary.includedCreditsRemaining} credits left in this cycle`;
  }
  if ((Number(summary.walletCreditsRemaining) || 0) > 0) {
    return `${summary.walletCreditsRemaining} wallet credits left`;
  }
  return 'No credits left';
};

export const getAiPlanLabel = (summary = {}) => {
  if (summary.planTier === 'ai_plus') return 'AI Plus';
  if (summary.planTier === 'trial') return 'AI Trial';
  return 'Core';
};

export const getAiAccessSummary = () => authorizedPost('getAiAccessSummaryHttp');
export const activateAiTrial = () => authorizedPost('activateAiTrialHttp');
export const subscribeAiPlus = async () => {
  const scriptReady = await loadRazorpayScript();
  if (!scriptReady || !window.Razorpay) {
    throw new Error('Unable to load secure checkout.');
  }

  const checkout = await authorizedPost('subscribeAiPlusHttp');
  const payment = await openRazorpayCheckout({
    key: checkout.key,
    subscription_id: checkout.subscriptionId,
    name: 'iAdvocate',
    description: `Premium Access - Rs ${AI_PLUS_PRICE_INR}/month`,
    prefill: {
      name: checkout.customer?.name || '',
      email: checkout.customer?.email || '',
      contact: checkout.customer?.contact || '',
    },
    theme: {
      color: '#1144cc',
    },
  });

  return authorizedPost('verifyAiPlusSubscriptionHttp', {
    subscriptionId: payment.razorpay_subscription_id,
    paymentId: payment.razorpay_payment_id,
    signature: payment.razorpay_signature,
  });
};
export const cancelAiPlusRenewal = () => authorizedPost('cancelAiPlusRenewalHttp');
export const createWalletTopup = async (packId) => {
  const scriptReady = await loadRazorpayScript();
  if (!scriptReady || !window.Razorpay) {
    throw new Error('Unable to load secure checkout.');
  }

  const checkout = await authorizedPost('createWalletTopupHttp', { packId });
  const payment = await openRazorpayCheckout({
    key: checkout.key,
    order_id: checkout.orderId,
    amount: checkout.amountPaise,
    currency: checkout.currency || 'INR',
    name: 'iAdvocate',
    description: `${checkout.pack?.credits || 0} credits top-up`,
    prefill: {
      name: checkout.customer?.name || '',
      email: checkout.customer?.email || '',
      contact: checkout.customer?.contact || '',
    },
    theme: {
      color: '#1144cc',
    },
  });

  return authorizedPost('verifyWalletTopupHttp', {
    topupOrderId: checkout.topupOrderId,
    orderId: payment.razorpay_order_id,
    paymentId: payment.razorpay_payment_id,
    signature: payment.razorpay_signature,
  });
};
export const estimateAiUsage = (payload) => authorizedPost('estimateAiUsageHttp', payload);
